// @file SortableJS options
// https://github.com/SortableJS/Sortable#options
import window from '@@/bits/global'
import type { Group } from '@@/bits/surface_posts_grouping_helper'
import type { Cid, Id, WallViz } from '@@/types'
import type Sortable from 'sortablejs'
import type { SortableEvent, Options as SortableOptions } from 'sortablejs'

const WALL_CONTAINER_ID = 'wall-container'
const WISH_LIST_ID = 'wish-list'
const PERSISTENT_CLONE_CLASS = 'sortable-persistent-clone'
const INDICATOR_LINE_CLASS = 'sortable-indicator-line'
const NO_DRAG_SELECTOR = '.no-drag, input, textarea'
const SORTABLE_GROUP_SELECTOR = '.group-posts'

/**********************
 * SortableJS options *
 **********************/

const viewportHeight = window.visualViewport.height
const viewportWidth = window.visualViewport.width
const isSmallerThanTabletLandscape = viewportWidth < 1024

const getScrollSensitivity = (direction: 'vertical' | 'horizontal', defaultSens: number): number => {
  const widthOrHeight = direction === 'vertical' ? viewportHeight : viewportWidth
  return Math.min(defaultSens, widthOrHeight / 6)
}

const getScrollSpeed = (direction: 'vertical' | 'horizontal', defaultSpeed: number): number => {
  const widthOrHeight = direction === 'vertical' ? viewportHeight : viewportWidth
  return Math.min(defaultSpeed, widthOrHeight / 20)
}

const postSortableOptions: {
  default: SortableOptions
} & {
  [K in WallViz]?: SortableOptions
} = {
  default: {
    // We disable animation by default because the only thing that's animated by SortableJS now is the
    // indicator line, but that's somewhat subtle. By disabling animation, we improve performance a bit
    // on low-end devices.
    animation: 0,
    invertSwap: true,
    // Only swap when dragged on edge of 25% of the post at target location. 10% for small screens.
    invertedSwapThreshold: isSmallerThanTabletLandscape ? 0.2 : 0.5,
    // Don't use native drag n drop because:
    // - It has a bug with :hover style (https://bugs.chromium.org/p/chromium/issues/detail?id=410328)
    // - It doesn't auto scroll when dragging an element to the edge of the container
    // - It doesn't support using scroll wheel while dragging
    forceFallback: true,
    fallbackOnBody: true,
    fallbackTolerance: 5,
    delay: 150,
    delayOnTouchOnly: true,
    scroll: true,
    scrollSensitivity: getScrollSensitivity('horizontal', 150),
    scrollSpeed: getScrollSpeed('horizontal', 30),
  },
  stream: {
    // Stream posts are dragged vertically.
    scrollSensitivity: getScrollSensitivity('vertical', 150),
    scrollSpeed: getScrollSpeed('vertical', 25),
  },
  grid: {
    // Grid posts are dragged vertically.
    scrollSensitivity: getScrollSensitivity('vertical', 50),
    scrollSpeed: getScrollSpeed('vertical', 20),
  },
  map: {
    // For map we retain animation because the post/section itself is animated. We don't have indicator line
    // there.
    // Still disable animation for small screens - most of them are phones - for performance reason.
    animation: isSmallerThanTabletLandscape ? 0 : 150,
    invertSwap: false,
    swapThreshold: isSmallerThanTabletLandscape ? 0.5 : 0.75,
    // Map posts are vertical when > tablet landscape, horizontal when < tablet landscape.
    scrollSensitivity: isSmallerThanTabletLandscape
      ? getScrollSensitivity('horizontal', 100)
      : getScrollSensitivity('vertical', 150),
    scrollSpeed: isSmallerThanTabletLandscape ? getScrollSpeed('horizontal', 20) : getScrollSpeed('vertical', 25),
  },
}

// Section options are similar to post options, except scroll sensitivity and speed since sections
// can be in different direction than posts.
const sectionSortableOptions: typeof postSortableOptions = {
  // Most formats have sections lined up vertically.
  default: {
    ...postSortableOptions.default,
    scrollSensitivity: getScrollSensitivity('vertical', 150),
    scrollSpeed: getScrollSpeed('vertical', 25),
  },
  // Except grid.
  grid: {
    scrollSensitivity: getScrollSensitivity('horizontal', 150),
    scrollSpeed: getScrollSpeed('horizontal', 30),
  },
  // Bring map-specific options here.
  map: {
    ...(postSortableOptions.map ?? {}),
    animation: 0,
    invertSwap: true,
    invertedSwapThreshold: isSmallerThanTabletLandscape ? 0.2 : 0.5,
  },
}

// Merge config from left to right:
// default config + format specific config
const getSortableOptions = (dragElement: 'post' | 'section', format: WallViz | 'default'): SortableOptions => {
  const opts = dragElement === 'post' ? postSortableOptions : sectionSortableOptions
  return { ...opts.default, ...(opts[format] ?? {}) }
}

/*******************
 * Dragging styles *
 *******************/

const getIndicatorColor = (isDarkMode: boolean): string => {
  // Selection light/dark color
  return isDarkMode ? '#FEC006' : '#9466E8'
}

// SortableJS creates an element that is moved around with the mouse cursor while dragging.
// This element doesn't have direction properly set, so we need to set it manually.
const setDirectionForDraggingElement = (dir: 'ltr' | 'rtl'): void => {
  requestAnimationFrame(() => {
    const draggedElement = document.querySelector('.sortable-fallback')
    if (draggedElement != null) {
      draggedElement.setAttribute('dir', dir)
    }
  })
}

const createAndInsertCloneAtOriginalPosition = (
  sortableEvent: SortableEvent,
  cloneElementSelector: string,
): {
  cloneWrapperElement: HTMLElement
  cloneTargetElement: HTMLElement
} => {
  const draggedElement = sortableEvent.item
  const originalPosition = Math.max(sortableEvent.oldIndex ?? 0, 0)
  const cloneWrapperElement = sortableEvent.from.insertBefore(
    draggedElement.cloneNode(true),
    sortableEvent.from.children[originalPosition],
  ) as HTMLElement

  const cloneTargetElement = cloneWrapperElement.querySelector(cloneElementSelector) as HTMLElement

  cloneWrapperElement.classList.add(PERSISTENT_CLONE_CLASS)
  cloneWrapperElement.classList.remove(INDICATOR_LINE_CLASS)

  return { cloneWrapperElement, cloneTargetElement }
}

/*******************
 * Dragging events *
 *******************/

interface SectionDragEvent {
  groupId: string
}

interface PostDragEvent {
  postCid?: Cid
  postId?: Id
  groupId?: Group['id']
  newIndex?: number
}

const toSectionDragEvent = (e: SortableEvent): SectionDragEvent => {
  const groupId = e.item.dataset.id ?? ''
  return { groupId }
}

const toPostDragEvent = (e: SortableEvent): PostDragEvent => {
  const postCid = e.item.dataset.cid ?? ''
  const postId = parseInt(e.item.dataset.id ?? '')
  const groupIdAsNumber = parseInt(e.to.dataset.id ?? '')
  const groupId = Number.isNaN(groupIdAsNumber) ? e.to.dataset.id ?? '' : groupIdAsNumber
  if (postCid === '' || Number.isNaN(postId) || groupId === '') {
    return {}
  }
  return { postCid, postId, groupId }
}

/**
 * Determines whether the given element is a container for a group of posts
 */
function isElementPostGroup(e: HTMLElement): boolean {
  return e.classList.contains(SORTABLE_GROUP_SELECTOR.slice(1))
}

/**
 * Determines whether the move event represents a drag to the top of the post group
 */
function isDraggedToTopOfGroup(event: Sortable.MoveEvent): boolean {
  const groupElement = event.to
  const relatedPostElement = event.related

  return (
    // we are dragging over the first post in the group
    (groupElement.querySelector('.wish-wrapper:first-child') === relatedPostElement ||
      // selector in map post list
      groupElement.querySelector('[id^=wish-preview-]:first-child') === relatedPostElement) &&
    // we are dragging to _before_ the first post
    event.willInsertAfter === false
  )
}

/**
 * Determines whether the move event represents a drag to the bottom of the post group
 */
function isDraggedToBottomOfGroup(event: Sortable.MoveEvent): boolean {
  const groupElement = event.to
  const relatedPostElement = event.related
  return (
    // we are dragging over the last post in the group
    (groupElement.querySelector('.wish-wrapper:last-child') === relatedPostElement ||
      // selector in map post list
      groupElement.querySelector('[id^=wish-preview-]:last-child') === relatedPostElement) &&
    // we are dragging to _after_ the last post
    event.willInsertAfter === true
  )
}

export {
  INDICATOR_LINE_CLASS,
  NO_DRAG_SELECTOR,
  PERSISTENT_CLONE_CLASS,
  SORTABLE_GROUP_SELECTOR,
  WALL_CONTAINER_ID,
  WISH_LIST_ID,
  createAndInsertCloneAtOriginalPosition,
  getIndicatorColor,
  getSortableOptions,
  isDraggedToBottomOfGroup,
  isDraggedToTopOfGroup,
  isElementPostGroup,
  setDirectionForDraggingElement,
  toPostDragEvent,
  toSectionDragEvent,
}
export type { PostDragEvent, SectionDragEvent }
